/**
 * Statement PDF Template
 *
 * Generates an account statement PDF for a customer, showing a chronological
 * list of transactions with opening/closing balances.
 *
 * Used by GetStatementModal to produce downloadable customer statements.
 */

import React from 'react'
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer'

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Times-Roman',
    fontSize: 10,
    paddingTop: 35,
    paddingLeft: 35,
    paddingRight: 35,
    paddingBottom: 65,
    lineHeight: 1.5,
  },

  header: {
    flexDirection: 'row',
    marginBottom: 20,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
  },

  headerLeft: {
    flex: 1,
  },

  headerRight: {
    flex: 1,
    alignItems: 'flex-end',
  },

  logo: {
    maxWidth: 100,
    maxHeight: 80,
    marginBottom: 10,
    objectFit: 'contain',
  },

  companyName: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 5,
  },

  companyInfo: {
    fontSize: 9,
    lineHeight: 1.3,
    marginBottom: 2,
  },

  statementTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'right',
  },

  statementDetails: {
    fontSize: 10,
    textAlign: 'right',
    lineHeight: 1.4,
  },

  customerSection: {
    marginBottom: 20,
  },

  customerName: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 4,
  },

  customerText: {
    fontSize: 10,
    lineHeight: 1.4,
    marginBottom: 2,
  },

  periodText: {
    fontSize: 10,
    marginTop: 4,
    fontStyle: 'italic',
    color: '#444444',
  },

  // Transaction table
  table: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#cccccc',
    marginBottom: 15,
  },

  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f0f0f0',
    borderBottomWidth: 1,
    borderBottomColor: '#cccccc',
    padding: 5,
  },

  headerText: {
    fontSize: 9,
    fontWeight: 'bold',
  },

  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    padding: 5,
  },

  tableRowAlt: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    padding: 5,
    backgroundColor: '#fafafa',
  },

  tableRowClosing: {
    flexDirection: 'row',
    borderBottomWidth: 0,
    padding: 5,
    backgroundColor: '#f0f0f0',
    borderTopWidth: 1,
    borderTopColor: '#999999',
  },

  tableCell: {
    fontSize: 9,
  },

  tableCellBold: {
    fontSize: 9,
    fontWeight: 'bold',
  },

  tableCellItalic: {
    fontSize: 9,
    fontStyle: 'italic',
  },

  // Column widths
  colDate: { width: '14%' },
  colRef: { width: '28%' },
  colTotal: { width: '15%', textAlign: 'right' },
  colPaid: { width: '15%', textAlign: 'right' },
  colCredited: { width: '15%', textAlign: 'right' },
  colDue: { width: '13%', textAlign: 'right' },

  footer: {
    position: 'absolute',
    bottom: 30,
    left: 35,
    right: 35,
    textAlign: 'center',
    borderTopWidth: 1,
    borderTopColor: '#000000',
    paddingTop: 10,
  },

  footerText: {
    fontSize: 9,
    color: '#666666',
    marginBottom: 3,
  },

  pageNumber: {
    fontSize: 9,
    color: '#666666',
  },
})

const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0
  return `£${num.toFixed(2)}`
}

const formatDate = (dateString) => {
  if (!dateString) return '—'
  // Handle YYYY-MM-DD strings without timezone conversion
  const parts = dateString.split('T')[0].split('-')
  if (parts.length === 3) {
    const [year, month, day] = parts
    return `${day}/${month}/${year}`
  }
  return dateString
}

const StatementTemplate = ({ statementData, farmInfo, logoUrl }) => {
  if (!statementData) return null

  const {
    customer_name,
    contact_name,
    from_date,
    to_date,
    opening_balance,
    entries = [],
    closing_balance,
  } = statementData

  const generatedDate = formatDate(new Date().toISOString().split('T')[0])

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {logoUrl && (
              <View style={{ marginBottom: 10 }}>
                <Image style={styles.logo} src={logoUrl} />
              </View>
            )}
            {farmInfo?.business_name && (
              <Text style={styles.companyName}>{farmInfo.business_name}</Text>
            )}
            {farmInfo?.address_line_1 && (
              <Text style={styles.companyInfo}>{farmInfo.address_line_1}</Text>
            )}
            {farmInfo?.address_line_2 && (
              <Text style={styles.companyInfo}>{farmInfo.address_line_2}</Text>
            )}
            {farmInfo?.city && (
              <Text style={styles.companyInfo}>
                {farmInfo.city}{farmInfo.postcode ? `, ${farmInfo.postcode}` : ''}
              </Text>
            )}
            {farmInfo?.phone && (
              <Text style={styles.companyInfo}>Tel: {farmInfo.phone}</Text>
            )}
            {farmInfo?.email && (
              <Text style={styles.companyInfo}>Email: {farmInfo.email}</Text>
            )}
          </View>

          <View style={styles.headerRight}>
            <Text style={styles.statementTitle}>ACCOUNT STATEMENT</Text>
            <Text style={styles.statementDetails}>Generated: {generatedDate}</Text>
          </View>
        </View>

        {/* Customer info */}
        <View style={styles.customerSection}>
          <Text style={styles.customerName}>{customer_name}</Text>
          {contact_name && contact_name !== customer_name && (
            <Text style={styles.customerText}>Attn: {contact_name}</Text>
          )}
          <Text style={styles.periodText}>
            Statement period: {formatDate(from_date)} to {formatDate(to_date)}
          </Text>
        </View>

        {/* Transaction table */}
        <View style={styles.table}>
          {/* Table header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.headerText, styles.colDate]}>Date</Text>
            <Text style={[styles.headerText, styles.colRef]}>Reference</Text>
            <Text style={[styles.headerText, styles.colTotal]}>Total</Text>
            <Text style={[styles.headerText, styles.colPaid]}>Paid</Text>
            <Text style={[styles.headerText, styles.colCredited]}>Credited</Text>
            <Text style={[styles.headerText, styles.colDue]}>Due</Text>
          </View>

          {/* Opening balance row */}
          {opening_balance !== 0 && (
            <View style={styles.tableRow}>
              <Text style={[styles.tableCellBold, styles.colDate]}></Text>
              <Text style={[styles.tableCellBold, styles.colRef]}>Opening Balance</Text>
              <Text style={[styles.tableCellBold, styles.colTotal]}></Text>
              <Text style={[styles.tableCellBold, styles.colPaid]}></Text>
              <Text style={[styles.tableCellBold, styles.colCredited]}></Text>
              <Text style={[styles.tableCellBold, styles.colDue]}>{formatCurrency(opening_balance)}</Text>
            </View>
          )}

          {/* Transaction rows */}
          {entries
            .filter(e => e.type !== 'opening_balance')
            .map((entry, index) => {
              const isCreditNote = entry.type === 'credit_note'
              const isAlt = index % 2 === 1
              const rowStyle = isAlt ? styles.tableRowAlt : styles.tableRow
              const refStyle = isCreditNote ? styles.tableCellItalic : styles.tableCell

              return (
                <View key={index} style={rowStyle}>
                  <Text style={[styles.tableCell, styles.colDate]}>{formatDate(entry.date)}</Text>
                  <Text style={[refStyle, styles.colRef]}>{entry.reference || '—'}</Text>
                  <Text style={[styles.tableCell, styles.colTotal]}>
                    {entry.total != null && entry.total !== 0 ? formatCurrency(entry.total) : ''}
                  </Text>
                  <Text style={[styles.tableCell, styles.colPaid]}>
                    {entry.paid != null && entry.paid !== 0 ? formatCurrency(entry.paid) : ''}
                  </Text>
                  <Text style={[styles.tableCell, styles.colCredited]}>
                    {entry.credited != null && entry.credited !== 0 ? formatCurrency(entry.credited) : ''}
                  </Text>
                  <Text style={[styles.tableCell, styles.colDue]}>
                    {entry.due != null && entry.due !== 0 ? formatCurrency(entry.due) : ''}
                  </Text>
                </View>
              )
            })}

          {/* Closing balance row */}
          <View style={styles.tableRowClosing}>
            <Text style={[styles.tableCellBold, styles.colDate]}></Text>
            <Text style={[styles.tableCellBold, styles.colRef]}>Closing Balance</Text>
            <Text style={[styles.tableCellBold, styles.colTotal]}></Text>
            <Text style={[styles.tableCellBold, styles.colPaid]}></Text>
            <Text style={[styles.tableCellBold, styles.colCredited]}></Text>
            <Text style={[styles.tableCellBold, styles.colDue]}>{formatCurrency(closing_balance)}</Text>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>Statement generated by Odin</Text>
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          />
        </View>
      </Page>
    </Document>
  )
}

export default StatementTemplate
